import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShoppingCart, ArrowLeft, LogOut, Plus, Trash2, Loader2, Download,
  Upload, FileText, TrendingUp, TrendingDown, DollarSign, X, Edit,
} from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
         LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const parseError = (err, fallback = 'Error') => {
  const d = err?.response?.data?.detail;
  if (Array.isArray(d)) return d.map(x => x?.msg || x).join(' | ');
  return d || err?.message || fallback;
};
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CATEGORIES = ['servicios', 'insumos', 'sueldos', 'impuestos', 'marketing', 'infraestructura', 'otros'];
const CAT_COLORS = {
  servicios: '#5DA5D8', insumos: '#A78BFA', sueldos: '#4C8C3B',
  impuestos: '#F59E0B', marketing: '#EC4899', infraestructura: '#06B6D4', otros: '#9CA3AF',
};

const ComprasPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState('cashflow');
  const [purchases, setPurchases] = useState([]);
  const [plans, setPlans] = useState([]);
  const [cf, setCf] = useState(null);
  const [weeks, setWeeks] = useState(12);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored) { navigate('/login'); return; }
    const u = JSON.parse(stored);
    const allowed = ['admin', 'gerencia', 'finanzas'].includes(u.role)
      || u.username === 'Gerencia'
      || (u.extra_modules || []).includes('compras');
    if (!allowed) { toast.error('Acceso restringido'); navigate('/login'); return; }
    setUser(u);
  }, [navigate]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, planRes, cfRes] = await Promise.all([
        axios.get(`${API}/compras`, { headers: authHeaders() }),
        axios.get(`${API}/finance/payment-plans`, { headers: authHeaders() }),
        axios.get(`${API}/compras/cashflow?weeks=${weeks}`, { headers: authHeaders() }),
      ]);
      setPurchases(pRes.data || []);
      setPlans(planRes.data || []);
      setCf(cfRes.data);
    } catch (e) { toast.error(parseError(e)); }
    finally { setLoading(false); }
  }, [weeks]);

  useEffect(() => { if (user) loadAll(); }, [user, loadAll]);

  const deletePurchase = async (p) => {
    if (!window.confirm(`Eliminar compra "${p.concept}"?`)) return;
    try {
      await axios.delete(`${API}/compras/${p.id}`, { headers: authHeaders() });
      toast.success('Eliminada'); loadAll();
    } catch (e) { toast.error(parseError(e)); }
  };

  const exportCashflow = async () => {
    try {
      const r = await axios.get(`${API}/compras/cashflow/export/xlsx?weeks=${weeks}`,
        { headers: authHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `flujo_caja_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click(); URL.revokeObjectURL(url);
      toast.success('Excel descargado');
    } catch (e) { toast.error(parseError(e)); }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="compras-page">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/finanzas')}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Finanzas
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#4C8C3B] flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-[#2F3D8E]">Compras y Flujo de Caja</h1>
                <p className="text-sm text-gray-500">Registro de gastos operativos + utilidad semanal</p>
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => { localStorage.clear(); navigate('/login'); }}>
            <LogOut className="w-4 h-4 mr-2" /> Salir
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-5">
        <div className="flex gap-1 bg-white rounded-xl border p-1 mb-4 w-fit">
          <button onClick={() => setTab('cashflow')}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium ${tab === 'cashflow' ? 'bg-[#2F3D8E] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                  data-testid="tab-cashflow">Flujo de Caja</button>
          <button onClick={() => setTab('purchases')}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium ${tab === 'purchases' ? 'bg-[#2F3D8E] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                  data-testid="tab-purchases">Compras ({purchases.length})</button>
        </div>

        {loading ? (
          <div className="text-center py-16"><Loader2 className="w-7 h-7 animate-spin mx-auto text-[#2F3D8E]" /></div>
        ) : tab === 'cashflow' ? (
          <CashFlowTab cf={cf} weeks={weeks} setWeeks={setWeeks} onExport={exportCashflow} />
        ) : (
          <PurchasesTab
            purchases={purchases} plans={plans}
            onCreate={() => setShowCreate(true)} onDelete={deletePurchase} onChanged={loadAll}
          />
        )}
      </main>

      {showCreate && (
        <NewPurchaseModal
          plans={plans}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); loadAll(); }}
        />
      )}
    </div>
  );
};

// ============================================================================
// CASH FLOW TAB
// ============================================================================
const CashFlowTab = ({ cf, weeks, setWeeks, onExport }) => {
  if (!cf) return <div className="text-gray-400 text-center py-12">Sin datos</div>;
  const t = cf.totals;
  const profitColor = t.utilidad >= 0 ? 'text-green-600' : 'text-red-600';

  return (
    <div className="space-y-4">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-green-50 rounded-xl p-4" data-testid="kpi-ingresos">
          <div className="text-xs uppercase text-green-700 font-semibold flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Ingresos</div>
          <div className="text-2xl font-bold text-green-700">{fmtMoney(t.ingresos)}</div>
          <div className="text-[10px] text-gray-500 mt-1">{cf.incomes_count} pagos recibidos</div>
        </div>
        <div className="bg-red-50 rounded-xl p-4" data-testid="kpi-gastos">
          <div className="text-xs uppercase text-red-700 font-semibold flex items-center gap-1"><TrendingDown className="w-3 h-3" /> Gastos</div>
          <div className="text-2xl font-bold text-red-700">{fmtMoney(t.gastos)}</div>
          <div className="text-[10px] text-gray-500 mt-1">{cf.purchases_count} compras</div>
        </div>
        <div className={`${t.utilidad >= 0 ? 'bg-blue-50' : 'bg-amber-50'} rounded-xl p-4`} data-testid="kpi-utilidad">
          <div className="text-xs uppercase font-semibold flex items-center gap-1"><DollarSign className="w-3 h-3" /> Utilidad</div>
          <div className={`text-2xl font-bold ${profitColor}`}>{fmtMoney(t.utilidad)}</div>
          <div className="text-[10px] text-gray-500 mt-1">Ingresos - Gastos</div>
        </div>
        <div className="bg-purple-50 rounded-xl p-4" data-testid="kpi-utilidad-pct">
          <div className="text-xs uppercase text-purple-700 font-semibold">Margen</div>
          <div className={`text-2xl font-bold ${profitColor}`}>{t.utilidad_pct}%</div>
          <div className="text-[10px] text-gray-500 mt-1">Utilidad / Ingresos</div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white border rounded-xl p-3 flex items-center gap-3 flex-wrap">
        <Label className="text-xs">Ultimas:</Label>
        <select value={weeks} onChange={e => setWeeks(parseInt(e.target.value))}
                className="border rounded px-2 py-1 text-xs" data-testid="weeks-select">
          <option value={4}>4 semanas</option>
          <option value={8}>8 semanas</option>
          <option value={12}>12 semanas</option>
          <option value={26}>26 semanas (~6 meses)</option>
          <option value={52}>52 semanas (1 año)</option>
        </select>
        <span className="text-[11px] text-gray-500 ml-auto">
          Periodo: {cf.range.start} → {cf.range.end}
        </span>
        <Button size="sm" onClick={onExport} className="bg-[#4C8C3B] hover:bg-[#3d7030] text-white"
                data-testid="export-cashflow-btn">
          <Download className="w-4 h-4 mr-1.5" /> Excel
        </Button>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border p-4 lg:col-span-2">
          <h3 className="font-bold text-sm text-gray-700 mb-3">Ingresos vs Gastos · Utilidad semanal</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={cf.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="week" tick={{ fontSize: 9 }} angle={-25} dy={10} height={50} interval={0} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="ingresos" fill="#4C8C3B" name="Ingresos" radius={[3, 3, 0, 0]} />
                <Bar dataKey="gastos" fill="#DC2626" name="Gastos" radius={[3, 3, 0, 0]} />
                <Bar dataKey="utilidad" fill="#5DA5D8" name="Utilidad" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-xl border p-4">
          <h3 className="font-bold text-sm text-gray-700 mb-3">Gastos por categoria</h3>
          {(cf.purchases_by_category || []).length === 0 ? (
            <div className="h-72 flex items-center justify-center text-gray-400 text-xs italic">Sin gastos en el periodo</div>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={cf.purchases_by_category} dataKey="amount" nameKey="category"
                       outerRadius={85} label={({ category, amount }) => `${category}: $${amount}`}
                       labelLine={false} stroke="#fff">
                    {cf.purchases_by_category.map((e, i) => (
                      <Cell key={i} fill={CAT_COLORS[e.category] || '#999'} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Utilidad % line */}
      <div className="bg-white rounded-xl border p-4">
        <h3 className="font-bold text-sm text-gray-700 mb-3">Margen de utilidad semanal (%)</h3>
        <div className="h-40">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={cf.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="week" tick={{ fontSize: 9 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
              <Tooltip formatter={(v) => `${v}%`} contentStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="utilidad_pct" stroke="#7C3AED" strokeWidth={2} dot={{ r: 3 }} name="Margen %" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// PURCHASES TAB
// ============================================================================
const PurchasesTab = ({ purchases, plans, onCreate, onDelete }) => {
  const plansById = plans.reduce((a, p) => ({ ...a, [p.id]: p }), {});
  return (
    <div className="space-y-3" data-testid="purchases-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-800">Compras registradas ({purchases.length})</h2>
        <Button onClick={onCreate} className="bg-[#2F3D8E] hover:bg-[#1f2d6e] text-white" data-testid="new-purchase-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Nueva compra
        </Button>
      </div>
      {purchases.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center text-gray-400">
          <ShoppingCart className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p>Aun no hay compras registradas.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Fecha</th>
                <th className="px-3 py-2 text-left">Proveedor</th>
                <th className="px-3 py-2 text-left">Concepto</th>
                <th className="px-3 py-2 text-left">Categoria</th>
                <th className="px-3 py-2 text-right">Monto</th>
                <th className="px-3 py-2 text-left">Plan rel.</th>
                <th className="px-3 py-2 text-center">Factura</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {purchases.map(p => (
                <tr key={p.id} className="border-t hover:bg-gray-50" data-testid={`purchase-row-${p.id}`}>
                  <td className="px-3 py-2 font-mono text-[11px]">{p.date}</td>
                  <td className="px-3 py-2">{p.supplier}</td>
                  <td className="px-3 py-2 text-gray-700">{p.concept}
                    {p.invoice_number && <div className="text-[10px] text-gray-400">Fact: {p.invoice_number}</div>}
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: (CAT_COLORS[p.category] || '#999') + '22', color: CAT_COLORS[p.category] || '#444' }}>
                      {p.category}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-semibold">{fmtMoney(p.amount)}</td>
                  <td className="px-3 py-2 text-[11px] text-gray-500">
                    {p.related_payment_plan_id && plansById[p.related_payment_plan_id]
                      ? `${plansById[p.related_payment_plan_id].contract_number} · ${plansById[p.related_payment_plan_id].client_company_name}`
                      : '-'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {p.invoice_pdf_url ? (
                      <a href={p.invoice_pdf_url} target="_blank" rel="noreferrer" className="text-red-600 hover:underline inline-flex items-center gap-1 text-[11px]" data-testid={`invoice-link-${p.id}`}>
                        <FileText className="w-3.5 h-3.5" /> PDF
                      </a>
                    ) : <span className="text-gray-300 text-[11px]">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => onDelete(p)} className="text-red-400 hover:text-red-600" data-testid={`delete-purchase-${p.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// NEW PURCHASE MODAL
// ============================================================================
const NewPurchaseModal = ({ plans, onClose, onCreated }) => {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    supplier: '', concept: '', category: 'otros', amount: '',
    related_payment_plan_id: '', invoice_number: '', notes: '',
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.supplier || !form.concept || !form.amount) { toast.error('Proveedor, concepto y monto requeridos'); return; }
    setBusy(true);
    try {
      const payload = {
        date: form.date, supplier: form.supplier, concept: form.concept,
        category: form.category, amount: parseFloat(form.amount),
        related_payment_plan_id: form.related_payment_plan_id || null,
        invoice_number: form.invoice_number || null,
        notes: form.notes || '',
      };
      if (pdfFile) {
        if (pdfFile.type !== 'application/pdf') { toast.error('Solo PDF'); setBusy(false); return; }
        if (pdfFile.size > 10 * 1024 * 1024) { toast.error('PDF excede 10MB'); setBusy(false); return; }
        payload.invoice_pdf_data_url = await new Promise((res, rej) => {
          const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(pdfFile);
        });
        payload.invoice_pdf_filename = pdfFile.name;
      }
      await axios.post(`${API}/compras`, payload, { headers: authHeaders() });
      toast.success('Compra registrada');
      onCreated?.();
    } catch (e) { toast.error(parseError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl shadow-xl" data-testid="new-purchase-modal">
        <div className="p-4 border-b flex items-center justify-between bg-gradient-to-r from-[#2F3D8E] to-[#4C8C3B] text-white rounded-t-xl">
          <h3 className="font-bold">Nueva Compra</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3 max-h-[75vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Fecha *</Label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}
                     data-testid="purchase-date-input" />
            </div>
            <div>
              <Label className="text-xs">Categoria *</Label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                      className="border rounded px-2 py-1.5 w-full text-sm" data-testid="purchase-category-select">
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Proveedor *</Label>
            <Input value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })}
                   placeholder="Ej: AWS, Google Workspace, Nomina..." data-testid="purchase-supplier-input" />
          </div>
          <div>
            <Label className="text-xs">Concepto *</Label>
            <Input value={form.concept} onChange={e => setForm({ ...form, concept: e.target.value })}
                   placeholder="Hosting mensual / Sueldo desarrollador / etc." data-testid="purchase-concept-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Monto USD *</Label>
              <Input type="number" step="0.01" value={form.amount}
                     onChange={e => setForm({ ...form, amount: e.target.value })}
                     placeholder="0.00" data-testid="purchase-amount-input" />
            </div>
            <div>
              <Label className="text-xs"># Factura proveedor</Label>
              <Input value={form.invoice_number} onChange={e => setForm({ ...form, invoice_number: e.target.value })}
                     placeholder="Opcional" data-testid="purchase-invoice-number-input" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Plan de pago relacionado (trazabilidad opcional)</Label>
            <select value={form.related_payment_plan_id} onChange={e => setForm({ ...form, related_payment_plan_id: e.target.value })}
                    className="border rounded px-2 py-1.5 w-full text-sm" data-testid="purchase-plan-select">
              <option value="">— Sin relacionar —</option>
              {plans.map(p => (
                <option key={p.id} value={p.id}>{p.contract_number} · {p.client_company_name}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Factura PDF (Cloudinary)</Label>
            <input type="file" accept="application/pdf" onChange={e => setPdfFile(e.target.files[0])}
                   className="text-xs" data-testid="purchase-pdf-input" />
            {pdfFile && <div className="text-[11px] text-gray-500 mt-1">{pdfFile.name} ({(pdfFile.size / 1024).toFixed(1)} KB)</div>}
          </div>
          <div>
            <Label className="text-xs">Notas</Label>
            <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                   placeholder="Opcional" />
          </div>
        </div>
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-2 rounded-b-xl">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#4C8C3B] hover:bg-[#3d7030] text-white"
                  data-testid="submit-purchase-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}
            Registrar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ComprasPage;
